"use client";

import { v4 as genUUID } from "uuid";
import React, { MouseEvent, ReactNode, useMemo } from "react";
import styled from "styled-components";

import PopupMenu, { MenuProps } from "./PopupMenu";

import Icon from "../Icon";
import ImageView from "../ImageView";
import LinkWrapper from "../LinkWrapper";
import Box from "../Box";
import Flex, { FlexProps } from "../Flex";
import { getColor, positioningCSS, getFont, getThemeColor } from "../utils";
import Tooltip from "../Tooltip";
import Text from "../Text";
import useBoolean from "../../hooks/useBoolean";
import { TinyColor } from "@ctrl/tinycolor";

export type ItemProps = {
  id?: string;
  title: string | React.ReactNode;
  subtitle?: string | ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  font?: string;
  disabled?: boolean;
  accessory?: React.ReactNode;
  checked?: boolean;
  link?: string;
  truncate?: boolean;
  image?: {
    src?: string;
    iconId?: string;
    imageStyle?: "circle" | "default";
    fill?: string;
    size?: string | number;
    width?: string | number;
    height?: string | number;
    iconFill?: string;
    iconSize?: string | number;
    color?: string;
  };
  bordered?: boolean;
  children?: React.ReactNode;
  badge?: string;
  tooltip?: string | React.ReactNode;
  alignItems?: FlexProps["alignItems"];
  hPos?: MenuProps["hPos"];
  isActive?: boolean;
  innerRef?: React.Ref<HTMLDivElement>;
  colorPreview?: string;
};

// Wes: I believe this consumes clicks on disabled/non-clickable PopupMenu items
// Without it the click is passed through the menu which closes the menu
function noOp(e: MouseEvent) {
  e.preventDefault();
  e.stopPropagation();
}

type WrapperProps = {
  disabled: boolean;
  isActive: boolean;
} & FlexProps;

const ItemWrapper = styled(Flex).attrs<WrapperProps>(props => {
  return {
    p: "8px",
    alignItems: props.alignItems || "center",
    background: props.isActive ? "grayLighter" : "white",
    textAlign: "left",
    variations: {
      "&:hover": {
        background: props.disabled ? "white" : "grayLighter",
      },
      "&:last-of-type": {
        borderBottom: "none",
      },
    },
    style: {
      cursor: props.disabled ? "not-allowed" : "pointer",
      opacity: props.disabled ? 0.5 : 1,
    },
  };
})<WrapperProps>`
  ${positioningCSS}
`;

const Pill = styled.span`
  background: ${getColor("info")};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  ${getFont("ui.smallBold", null)}
`;

type ColorPreviewProps = {
  color: string;
};

function useBorder(color: string) {
  if (color === "transparent") return true;
  const c = new TinyColor(color);
  return c.toHsl().l > 0.95;
}

const ColorPreview = styled.span<ColorPreviewProps>`
  height: 16px;
  width: 16px;
  border-radius: 2px;
  border: ${props => useBorder(props.color) && `1px solid ${getColor("grayLight")}`};
  background-color: ${props => getThemeColor(props.color, props.theme, "blue")};
  margin-right: 8px;
`;

const Item: React.FC<ItemProps> = React.forwardRef<HTMLDivElement, ItemProps>(
  (
    {
      id,
      title,
      subtitle,
      onClick,
      link,
      image,
      font = "ui.regular",
      children,
      checked = false,
      disabled = false,
      truncate = false,
      bordered = false,
      badge,
      tooltip,
      accessory,
      hPos,
      isActive = false,
      innerRef,
      ...other
    },
    ref
  ) => {
    const [nestedOpen, openNested, closeNested] = useBoolean(false);
    const nestedPopupKey = useMemo(() => genUUID(), []);

    function renderImage() {
      if (!image) return null;
      const { src, iconId, imageStyle, color, ...imageProps } = image;
      if (src) {
        const rounded = imageStyle === "circle" ? "50%" : "default";
        return (
          <ImageView
            mr="s"
            iconSize={imageProps.size ?? 16}
            icon={iconId}
            width={imageProps.width ?? 24}
            height={imageProps.height ?? 24}
            src={src}
            cover
            background="grayLighter"
            iconFill={imageProps.fill || "grayDark"}
            borderRadius={rounded}
            {...imageProps}
          />
        );
      }
      if (iconId) return <Icon mr="s" iconId={iconId} fill="black" {...imageProps} />;
      if (color) {
        return <ColorPreview color={color} />;
      }
      return null;
    }

    function renderContent(popupSource: string = null) {
      return (
        <LinkWrapper disabled={disabled} title={typeof title === "string" ? title : ""} link={link}>
          <ItemWrapper
            data-tooltip-source={id}
            data-testid={id}
            onClick={disabled ? null : onClick}
            data-popup-source={popupSource}
            disabled={disabled}
            borderBottom={bordered ? "default" : null}
            isActive={isActive}
            ref={ref || innerRef}
            {...other}>
            <Flex flex="1" alignItems="center">
              {checked ? (
                <Icon
                  flexShrink="0"
                  flexGrow="0"
                  iconId="checkmark"
                  fill="black"
                  mr="xs"
                  size="16"
                />
              ) : (
                <Box flexShrink="0" mr="xs" width="16" height="16" />
              )}
              {renderImage()}
              <Flex flexDirection="column" alignItems="flex-start">
                {typeof title === "string" ? (
                  <Text truncate={truncate} flex="1" title={title} font={font}>
                    {title}
                  </Text>
                ) : (
                  title
                )}
                {typeof subtitle === "string" ? (
                  <Text
                    truncate={truncate}
                    flex="1"
                    title={subtitle}
                    font="ui.tiny"
                    color="grayDark">
                    {subtitle}
                  </Text>
                ) : (
                  subtitle
                )}
              </Flex>
            </Flex>
            {accessory}
            {badge && (
              <>
                <Pill>{badge}</Pill>
              </>
            )}
            {tooltip && (
              <Tooltip width={230} direction="bottom" source={id}>
                {tooltip}
              </Tooltip>
            )}
          </ItemWrapper>
        </LinkWrapper>
      );
    }

    function renderAsNested() {
      accessory = accessory || <Icon iconId="triangle-collapsed" justifySelf="center" size="16" />;
      onClick = onClick || noOp;
      return (
        <Box onMouseEnter={openNested} onMouseLeave={closeNested}>
          {renderContent(nestedPopupKey)}
          {nestedOpen && (
            <PopupMenu
              hPos={hPos ? hPos : "floatRight"}
              vPos="alignTop"
              source={nestedPopupKey}
              close={closeNested}
              clickAwayClose={false}>
              {children}
            </PopupMenu>
          )}
        </Box>
      );
    }

    return children ? renderAsNested() : renderContent();
  }
);

Item.displayName = "Item";

export default Item;
